/**
 * CryptidZoo Engine - Core Engine Class
 * 
 * Main engine wrapper around Babylon.js providing:
 * - Engine initialization and lifecycle
 * - Render loop management
 * - System coordination
 */

import { Engine as BabylonEngine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { WebGPUEngine } from "@babylonjs/core/Engines/webgpuEngine";
import { EngineOptions, IEngine } from "./Engine.types";
import { SceneManager } from "../scene/SceneManager";
import { InputManager } from "../input/InputManager";

export class Engine implements IEngine {
    private _babylonEngine: BabylonEngine | WebGPUEngine;
    private _canvas: HTMLCanvasElement;
    private _isRunning: boolean = false;
    private _lastFrameTime: number = 0;
    private _deltaTime: number = 0;
    private _sceneManager: SceneManager;
    private _inputManager: InputManager;

    // Callbacks
    private _onUpdate?: (deltaTime: number) => void;
    private _onLoadingProgress?: (progress: number, message: string) => void;

    public get babylonEngine(): BabylonEngine | WebGPUEngine {
        return this._babylonEngine;
    }

    public get canvas(): HTMLCanvasElement {
        return this._canvas;
    }

    public get sceneManager(): SceneManager {
        return this._sceneManager;
    }

    public get inputManager(): InputManager {
        return this._inputManager;
    }

    public get deltaTime(): number {
        return this._deltaTime;
    }

    public get isRunning(): boolean {
        return this._isRunning;
    }

    constructor(canvas: HTMLCanvasElement, options?: EngineOptions) {
        this._canvas = canvas;
        this._babylonEngine = new BabylonEngine(canvas, options?.antialias ?? true, {
            preserveDrawingBuffer: options?.preserveDrawingBuffer ?? false,
            stencil: options?.stencil ?? true,
            disableWebGL2Support: options?.disableWebGL2 ?? false,
            powerPreference: options?.powerPreference ?? "high-performance",
        });

        // Initialize managers
        this._sceneManager = new SceneManager(this);
        this._inputManager = new InputManager(canvas);

        // Handle window resize
        window.addEventListener("resize", this._onResize.bind(this));

        this._updateLoadingProgress(10, "Engine initialized");
    }

    /**
     * Create engine with WebGPU support (async initialization)
     */
    public static async createWithWebGPU(
        canvas: HTMLCanvasElement,
        options?: EngineOptions
    ): Promise<Engine> {
        const engine = new Engine(canvas, options);
        
        // Try WebGPU if supported
        if (await WebGPUEngine.IsSupportedAsync) {
            const powerPref = options?.powerPreference === "low-power" ? "low-power" : "high-performance";
            const webGPUEngine = new WebGPUEngine(canvas, {
                powerPreference: powerPref as GPUPowerPreference,
            });
            await webGPUEngine.initAsync();
            engine._babylonEngine.dispose();
            engine._babylonEngine = webGPUEngine;
        }

        return engine;
    }

    /**
     * Set callback for update loop
     */
    public onUpdate(callback: (deltaTime: number) => void): void {
        this._onUpdate = callback;
    }

    /**
     * Set callback for loading progress
     */
    public onLoadingProgress(callback: (progress: number, message: string) => void): void {
        this._onLoadingProgress = callback;
    }

    /**
     * Start the game loop
     */
    public start(): void {
        if (this._isRunning) return;

        this._isRunning = true;
        this._lastFrameTime = performance.now();
        
        this._babylonEngine.runRenderLoop(() => {
            this._gameLoop();
        });

        this._updateLoadingProgress(100, "Ready");
    }

    /**
     * Stop the game loop
     */
    public stop(): void {
        this._isRunning = false;
        this._babylonEngine.stopRenderLoop();
    }

    /**
     * Get current active scene from Babylon
     */
    public getActiveScene(): Scene | null {
        return this._sceneManager.activeScene;
    }

    /**
     * Dispose engine and all resources
     */
    public dispose(): void {
        this.stop();
        window.removeEventListener("resize", this._onResize.bind(this));
        this._inputManager.dispose();
        this._sceneManager.dispose();
        this._babylonEngine.dispose();
    }

    /**
     * Main game loop
     */
    private _gameLoop(): void {
        const currentTime = performance.now();
        this._deltaTime = (currentTime - this._lastFrameTime) / 1000; // Convert to seconds
        this._lastFrameTime = currentTime;

        // Cap delta time to prevent spiral of death
        if (this._deltaTime > 0.1) {
            this._deltaTime = 0.1;
        }

        // Update input
        this._inputManager.update();

        // Update scene
        this._sceneManager.update(this._deltaTime);

        // Custom update callback
        if (this._onUpdate) {
            this._onUpdate(this._deltaTime);
        }

        // Render active scene
        const activeScene = this._sceneManager.activeScene;
        if (activeScene) {
            activeScene.render();
        }
    }

    /**
     * Handle window resize
     */
    private _onResize(): void {
        this._babylonEngine.resize();
    }

    /**
     * Update loading progress
     */
    private _updateLoadingProgress(progress: number, message: string): void {
        if (this._onLoadingProgress) {
            this._onLoadingProgress(progress, message);
        }
    }
}
